import { Box } from "@chakra-ui/react";
import { useInView } from "framer-motion";
import Image from "next/image";
import React, { useRef, useState } from "react";
import { useHeaderHeight } from "contexts/header-height";
import { type TypewriterTextProps } from "ui/typewriter";
import { TextTitle } from "ui/text";
import { CopilotTypewriter } from "../../copilot-typewriter";
import bgImageMobile from "../../../../../../public/assets/platform/campaign-intelligence/copilot-bg-mobile.png";
import bgImageBase from "../../../../../../public/assets/platform/campaign-intelligence/copilot-bg-base.png";
import bgImage1 from "../../../../../../public/assets/platform/campaign-intelligence/copilot-bg-1.png";
import bgImage2 from "../../../../../../public/assets/platform/campaign-intelligence/copilot-bg-2.png";
import { BackgroundImage, type BackgroundImageProps } from "./background-image";

const transitionDelay = 2200;

const typewriterSequence: Array<TypewriterTextProps & BackgroundImageProps> = [
  {
    text: "How many units of each product did I sell last month, grouped by country?",
    duration: 3.5,
    imageUrl: bgImage1,
    imageAlt: "Graph showing sales of each product by country.",
  },
  {
    text: "How have my cumulative sales of each product changed over time?",
    duration: 3.5,
    imageUrl: bgImage2,
    imageAlt: "Graph showing cumulative sales of each product over time.",
  },
];

export function CopilotAnimation() {
  const { headerHeight } = useHeaderHeight();
  const scope = useRef<HTMLDivElement>(null);
  const isInView = useInView(scope, { amount: 0.7 });
  const [activeImageIndex, setActiveImageIndex] = useState(-1);

  return (
    <Box
      bg="brand.background.dark"
      border="1px"
      borderColor="brand.background.dark"
      borderRadius="smaller"
      height={{ base: `calc(70vh)`, md: `calc(100vh - ${headerHeight}px)` }}
      maxH={{ base: "auto", md: "670px" }}
      pt={{ base: "big", md: "biggest" }}
      pb={{ base: "hugest", md: 0 }}
      px={{ base: 7, md: 12, lg: 20 }}
      pos="relative"
      overflow="hidden"
    >
      <Box display={{ base: "block", md: "none" }} pos="absolute" inset={0}>
        <Box pos="relative" boxSize="100%">
          <Image
            fill
            sizes="100%"
            src={bgImageMobile}
            alt="Graph showing sales of each product by country."
            style={{ objectFit: "cover", objectPosition: "bottom center" }}
          />
        </Box>
      </Box>

      <Box display={{ base: "none", md: "block" }}>
        <BackgroundImage isInView imageUrl={bgImageBase} imageAlt="Dark gradient representing an empty state." />

        {typewriterSequence.map(({ imageUrl, imageAlt }, index) => (
          <BackgroundImage
            key={imageUrl.src}
            isInView={activeImageIndex === index}
            imageUrl={imageUrl}
            imageAlt={imageAlt}
          />
        ))}
      </Box>

      <Box ref={scope} pos="relative" zIndex={1}>
        <TextTitle
          align="center"
          size="lg"
          theme="dark"
          chip={{ variant: "violet", text: "Copilot" }}
          title="Campaign smarter and faster with AI"
          description="Hightouch Copilot proactively surfaces insights, suggests optimizations, and lets you explore data via chat - instead of SQL."
          maxW="860px"
          descriptionMaxW="700px"
        />

        <Box display={{ base: "none", md: "block" }} mt={14} maxW={{ base: "100%", md: "680px" }} mx="auto">
          <CopilotTypewriter
            isLooped
            isAnimationPlaying={isInView}
            fontSize={{ base: "xs", md: "sm", lg: "md" }}
            lineHeight={{ base: "18px", md: "20px", lg: "24px" }}
            transitionDelay={transitionDelay}
            placeholder={{
              text: "What do you want to see?",
            }}
            textSequence={typewriterSequence.map(
              ({ text, duration }, index): TypewriterTextProps => ({
                text,
                duration,
                onStart() {
                  const startTimeout = setTimeout(() => {
                    setActiveImageIndex(index);
                  }, 3000);

                  return () => {
                    clearTimeout(startTimeout);
                  };
                },

                // Show only the base image when the last typewriter sequence is complete and it's reset to the placeholder state
                onComplete() {
                  if (index === typewriterSequence.length - 1) {
                    const endTimeout = setTimeout(() => {
                      setActiveImageIndex(-1);
                    }, transitionDelay);

                    return () => {
                      clearTimeout(endTimeout);
                    };
                  }
                },
              }),
            )}
          />
        </Box>
      </Box>
    </Box>
  );
}
